import React, { memo } from "react";

import { useField } from "formik";
import * as Yup from "yup";

import DeploymentBusinessTable from "components/Business/BusinessForm/DeploymentBusinessTable";
import { withNamespace } from "components/Form/FormikControls";

import { EmailRecipientsHeader } from "./EmailRecipientsHeader";
import { EmailRecipientsPanel } from "./EmailRecipientsPanel";

const emailRecipientBaseValidation: Yup.SchemaOf<
  Omit<EmailRecipient, "id" | "source">
> = Yup.object()
  .shape({
    firstName: Yup.string(),
    lastName: Yup.string(),
    email: Yup.string().email("Invalid email"),
    phoneNumber: Yup.string(),
    isAccountsRecipient: Yup.boolean(),
    isCommercialReportRecipient: Yup.boolean(),
    isComplianceReportRecipient: Yup.boolean(),
    isEnvdRecipient: Yup.boolean(),
  })
  .test("at-least-one-field", "At least one field must be filled", values => {
    if (!values) return true;

    // Check if at least one has a truthy value
    const { firstName, lastName, email, phoneNumber } = values;
    return !!(firstName || lastName || email || phoneNumber);
  });

const emailRecipientValidationFactory = () =>
  Yup.lazy(value =>
    Yup.object().shape({
      editingValue: emailRecipientBaseValidation
        .test(
          "is-contact-unique",
          "This contact already exists",
          (_, { createError, path }) => {
            const { editingValue, values } = value;
            if (!editingValue) return true;

            // show error on a field that has data entered.
            const errorField = editingValue.email
              ? "email"
              : editingValue.phoneNumber
                ? "phoneNumber"
                : editingValue.firstName
                  ? "firstName"
                  : "lastName";

            if (
              values
                .filter(
                  (contact: EmailRecipient) => contact.id !== editingValue.id,
                )
                .some((contact: EmailRecipient) =>
                  ["email", "phoneNumber", "firstName", "lastName"].every(
                    (fieldName: string) => {
                      const formValue: string =
                        editingValue[fieldName]?.trim() || "";
                      const contactValue: string =
                        contact[fieldName]?.trim() || "";
                      return formValue === contactValue;
                    },
                  ),
                )
            ) {
              return createError({
                path: `${path}.${errorField}`,
                message: "This contact already exists",
              });
            }

            return true;
          },
        )
        .nullable(),
    }),
  );

interface EmailRecipientsProps {
  masterBusinessId: string;
  primaryContactEmail: string;
  namespace: string;
  isOpen: boolean;
  onEndEditing: () => void;
  onStartEditing: () => void;
  onSetPrimaryContactEmail: (emailAddress: string) => void;
  onToggle: () => void;
}

const EmailRecipientsComponent: React.FC<EmailRecipientsProps> = ({
  masterBusinessId,
  primaryContactEmail,
  namespace: ns,
  isOpen,
  onEndEditing,
  onStartEditing,
  onSetPrimaryContactEmail,
  onToggle,
}) => {
  const emailRecipientsNs = withNamespace(ns, "emailRecipients");
  const emailRecipients = useField(emailRecipientsNs)[0].value;

  return (
    <DeploymentBusinessTable
      headerComponent={EmailRecipientsHeader}
      initialValuesList={emailRecipients}
      isOpen={isOpen}
      masterBusinessId={masterBusinessId}
      primaryContactEmail={primaryContactEmail}
      namespace={emailRecipientsNs}
      onEndEditing={onEndEditing}
      onStartEditing={onStartEditing}
      onSetPrimaryContactEmail={onSetPrimaryContactEmail}
      onToggle={onToggle}
      tableComponent={EmailRecipientsPanel}
      validationSchema={emailRecipientValidationFactory}
      dataTour="emailCollapse"
    />
  );
};

export default memo(EmailRecipientsComponent);
